import {
    reviewsApplicationLoggerToken,
    reviewsAuthApiClientToken,
} from "@apps/reviews/tokens";
import { applyTransaction } from "@datorama/akita";
import { JsonMimeType } from "@lib/ApiClient";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import {
    Observable,
    Subject,
    bufferTime,
    catchError,
    filter,
    lastValueFrom,
    map,
    mergeMap,
    tap,
} from "rxjs";
import { array, create } from "superstruct";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import ProductReviewInfoEntity from "../entities/ProductReviewInfoEntity";
import ProductReviewInfoSchema from "../schemas/ProductReviewInfoSchema";
import ListingProductInfoStore from "../stores/ListingProductInfoStore";

@singleton()
class ListingProductInfoService {
    private getListingProductInfoSubject = new Subject<number>();

    constructor(
        private readonly listingProductInfoStore: ListingProductInfoStore,
        @inject(reviewsAuthApiClientToken)
        private readonly apiClient: ApiClientInterface,
        @inject(reviewsApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {
        void lastValueFrom(this.subscribeToProductIds());
    }

    getBufferedListingProductInfo(productId: number): void {
        this.getListingProductInfoSubject.next(productId);
    }

    subscribeToProductIds(): Observable<ProductReviewInfoEntity[]> {
        applyTransaction(() => {
            this.listingProductInfoStore.setLoading(true);
            this.listingProductInfoStore.setError(null);
        });

        return this.getListingProductInfoSubject.pipe(
            bufferTime(50),
            filter((ids) => ids.length > 0),
            map((ids) => Array.from(new Set(ids))),
            mergeMap((uniqueIds) => {
                this.logger.debug(
                    "ReviewsService.getBufferedListingProductInfo is start"
                );
                return this.apiClient
                    .post({
                        url: "/review/getListingProductReviewInfo",
                        body: JSON.stringify({
                            productsIds: uniqueIds,
                        }),
                        responseType: "json",
                        contentType: JsonMimeType,
                    })
                    .pipe(
                        map((response) =>
                            create(
                                response.body,
                                array(ProductReviewInfoSchema)
                            )
                        ),
                        catchError((error: unknown) => {
                            this.logger.debug(
                                "ReviewsService.getBufferedListingProductInfo is errored",
                                error
                            );

                            applyTransaction(() => {
                                this.listingProductInfoStore.setLoading(false);
                                this.listingProductInfoStore.setError(error);
                            });

                            throw error;
                        }),
                        tap((entities) => {
                            this.logger.debug(
                                "ReviewsService.getBufferedListingProductInfo is ready"
                            );
                            applyTransaction(() => {
                                this.listingProductInfoStore.setLoading(false);
                                this.listingProductInfoStore.add(entities);
                            });
                        })
                    );
            })
        );
    }
}

export default ListingProductInfoService;
